import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Chip,
    Alert,
    CircularProgress,
    TablePagination,
    IconButton
} from '@mui/material';
import {
    Email as EmailIcon,
    Person as PersonIcon,
    DateRange as DateIcon,
    Assessment as AnalyticsIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';

const emptySummary = {
    totalOffers: 0,
    offersToday: 0,
    offersThisWeek: 0,
    topAgent: null,
    agentStats: []
};

function PerformanceReports() {
    const [offers, setOffers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [summary, setSummary] = useState(emptySummary);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 25,
        totalPages: 1
    });

    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        agentId: '',
        searchCustomer: '',
        quickFilter: ''
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await axios.get('/api/users');
            setUsers(response.data.users || []);
        } catch (error) {
            setError('Failed to load users for filters: ' + (error.response?.data?.error || error.message));
        }
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const response = await axios.get('/api/logs/offers-paginated', {
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                    fromDate: filters.dateFrom || undefined,
                    toDate: filters.dateTo || undefined,
                    agentId: filters.agentId || undefined,
                    search: filters.searchCustomer || undefined,
                    sort: 'sentAt_desc',
                    includeSummary: true
                }
            });

            setOffers(response.data.rows || []);
            setPagination({
                total: response.data.total || 0,
                page: response.data.page || page + 1,
                limit: response.data.limit || rowsPerPage,
                totalPages: response.data.totalPages || 1
            });
            setSummary(response.data.summary || emptySummary);
        } catch (error) {
            setError('Failed to load system reports data: ' + (error.response?.data?.error || error.message));
            setOffers([]);
            setPagination({ total: 0, page: 1, limit: rowsPerPage, totalPages: 1 });
            setSummary(emptySummary);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filters]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({
            dateFrom: '',
            dateTo: '',
            agentId: '',
            searchCustomer: '',
            quickFilter: ''
        });
        setPage(0);
    };

    const formatDateForInput = (date) => date.toISOString().split('T')[0];

    const setQuickDateFilter = (days) => {
        const now = new Date();
        const todayPT = new Date(now.toLocaleString('en-US', { timeZone: 'America/Vancouver' }));
        const todayPTMidnight = new Date(todayPT.getFullYear(), todayPT.getMonth(), todayPT.getDate());
        let pastDate;
        let quickFilterValue;

        if (days === 'today') {
            pastDate = todayPTMidnight;
            quickFilterValue = 'today';
        } else {
            pastDate = new Date(todayPTMidnight.getTime() - days * 24 * 60 * 60 * 1000);
            quickFilterValue = `${days}d`;
        }

        setFilters(prev => ({
            ...prev,
            dateFrom: formatDateForInput(pastDate),
            dateTo: formatDateForInput(todayPTMidnight),
            quickFilter: quickFilterValue
        }));
        setPage(0);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            timeZone: 'America/Vancouver',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        }) + ' PT';
    };

    const getUserName = (offer) => {
        if (offer.agentName) return offer.agentName;

        const user = users.find(u => u.id === offer.agentId);
        return user ? user.name : `User ${offer.agentId}`;
    };

    const handleDeleteOffer = async (offerId) => {
        if (!window.confirm('Are you sure you want to delete this sent offer? This action cannot be undone.')) {
            return;
        }

        try {
            setDeletingId(offerId);
            setError('');

            await axios.delete(`/api/logs/offer/${offerId}`);
            await fetchData();
        } catch (error) {
            setError('Failed to delete offer: ' + (error.response?.data?.error || error.message));
        } finally {
            setDeletingId(null);
        }
    };

    const activeAgentCount = users.filter(
        user => !user.isAdmin && user.isActive && user.accessProfile !== 'processing'
    ).length;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" gutterBottom>
                    Performance Reports
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Matching Offers
                                    </Typography>
                                    <Typography variant="h4">
                                        {summary.totalOffers}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <PersonIcon sx={{ mr: 1, color: 'success.main' }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Active Agents
                                    </Typography>
                                    <Typography variant="h4">
                                        {activeAgentCount}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <DateIcon sx={{ mr: 1, color: 'info.main' }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Today
                                    </Typography>
                                    <Typography variant="h4">
                                        {summary.offersToday}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <AnalyticsIcon sx={{ mr: 1, color: 'warning.main' }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        This Week
                                    </Typography>
                                    <Typography variant="h4">
                                        {summary.offersThisWeek}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {summary.topAgent && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Top Performer
                        {(filters.dateFrom || filters.dateTo || filters.quickFilter) && (
                            <Typography component="span" variant="subtitle2" color="textSecondary" sx={{ ml: 1 }}>
                                ({filters.quickFilter === 'today' ? 'Today' :
                                  filters.quickFilter ? filters.quickFilter.replace('d', ' Days') :
                                  'Selected Period'})
                            </Typography>
                        )}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <Chip
                            label={summary.topAgent.agentName}
                            color="primary"
                            sx={{ mr: 2 }}
                        />
                        <Typography variant="body2">
                            {summary.topAgent.totalOffers} offers sent
                        </Typography>
                    </Box>
                </Paper>
            )}

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Quick Date Filters:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {[
                            { days: 'today', label: 'Today' },
                            { days: 30, label: 'Last 30 Days' },
                            { days: 60, label: 'Last 60 Days' },
                            { days: 90, label: 'Last 90 Days' },
                            { days: 180, label: 'Last 180 Days' }
                        ].map(({ days, label }) => (
                            <Button
                                key={days}
                                variant={filters.quickFilter === (days === 'today' ? 'today' : `${days}d`) ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setQuickDateFilter(days)}
                            >
                                {label}
                            </Button>
                        ))}
                    </Box>
                </Box>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="From Date"
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="To Date"
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Agent</InputLabel>
                            <Select
                                value={filters.agentId}
                                onChange={(e) => handleFilterChange('agentId', e.target.value)}
                                label="Agent"
                            >
                                <MenuItem value="">All Agents</MenuItem>
                                {users.filter(user => !user.isAdmin && user.accessProfile !== 'processing').map(user => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Search Customer"
                            value={filters.searchCustomer}
                            onChange={(e) => handleFilterChange('searchCustomer', e.target.value)}
                            placeholder="Name, email, phone, subject..."
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Button
                            variant="outlined"
                            onClick={clearFilters}
                            fullWidth
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            Showing {offers.length} of {pagination.total} matching offers. Email body content is loaded only from detail views, not reports.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 1000 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date Sent</TableCell>
                                        <TableCell>Agent</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Template</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {offers.map((offer) => (
                                        <TableRow key={offer.id}>
                                            <TableCell>
                                                {formatDate(offer.sentAt)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getUserName(offer)}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{offer.customerFullName}</TableCell>
                                            <TableCell>{offer.customerEmail}</TableCell>
                                            <TableCell>{offer.customerPhone || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={offer.templateName || 'Unknown Template'}
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                                                    {offer.subjectLine}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteOffer(offer.id)}
                                                    disabled={deletingId === offer.id}
                                                    title={deletingId === offer.id ? 'Deleting...' : 'Delete offer'}
                                                >
                                                    {deletingId === offer.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!error && offers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                No matching offers found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={pagination.total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(parseInt(event.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Paper>

            {summary.agentStats.length > 0 && (
                <Paper sx={{ mt: 3 }}>
                    <Box p={2}>
                        <Typography variant="h6" gutterBottom>
                            Agent Performance
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Agent</TableCell>
                                        <TableCell align="center">Total Offers</TableCell>
                                        <TableCell>Last Activity</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {summary.agentStats.map((agent, index) => (
                                        <TableRow key={agent.agentId || index}>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    {index === 0 && (
                                                        <Typography sx={{ mr: 1 }}>
                                                            Top
                                                        </Typography>
                                                    )}
                                                    {agent.agentName}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={agent.totalOffers}
                                                    color={index === 0 ? 'primary' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {agent.lastSent ? formatDate(agent.lastSent) : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}

export default PerformanceReports;
